import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PatientAccessBlock } from "@/components/PatientAccessBlock";
import { fmtDate } from "@/lib/helpers";
import { Plus, Search, Users2, Pencil } from "lucide-react";
import { FrDateInput } from "@/components/FrDateInput";

export default function Patients() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ last_name: "", first_name: "", birth_date: "" });
  const [error, setError] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/patients", { params: search ? { search } : {} }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search]);

  const submit = async () => {
    setError("");
    if (!f.last_name.trim() || !f.first_name.trim()) { setError("Nom et prénom obligatoires."); return; }
    try { await api.post("/patients", { ...f, birth_date: f.birth_date || null, force_new: true }); toast.success("Patient enregistré"); setOpen(false); setF({ last_name: "", first_name: "", birth_date: "" }); load(); }
    catch (e) { setError(apiError(e.response?.data?.detail) || e.message); }
  };

  const saveEdit = async () => {
    try { await api.patch(`/patients/${edit.id}`, { birth_date: edit.birth_date || null }); toast.success("Date de naissance enregistrée"); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl font-heading font-extrabold text-brand-900">Patients</h1><p className="text-slate-500 mt-1">Fiche minimale — nom, prénom, date de naissance (facultative, complétable plus tard).</p></div>
        <Button data-testid="create-patient-button" onClick={() => setOpen(true)} className="bg-accent-600 hover:bg-accent-700 h-11"><Plus className="w-4 h-4 mr-1.5" />Nouveau patient</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
        <Input data-testid="patients-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, prénom, date)…" className="pl-9 h-11 bg-white" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>
            <th className="px-4 py-3 font-semibold">ID interne</th><th className="px-4 py-3 font-semibold">Nom</th><th className="px-4 py-3 font-semibold">Prénom</th><th className="px-4 py-3 font-semibold">Date de naissance</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => (
              <tr key={p.id} data-testid={`patient-row-${p.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-400 font-mono text-xs">{p.internal_id}</td>
                <td className="px-4 py-3 font-semibold text-slate-800">{p.last_name.toUpperCase()}</td>
                <td className="px-4 py-3 text-slate-600">{p.first_name}</td>
                <td className="px-4 py-3 text-slate-600"><span className="inline-flex items-center gap-2">{p.birth_date ? fmtDate(p.birth_date) : <span className="text-orange-600 font-semibold" data-testid={`dob-missing-${p.id}`}>Inconnue</span>}<button data-testid={`edit-dob-${p.id}`} onClick={() => setEdit({ id: p.id, name: `${p.last_name.toUpperCase()} ${p.first_name}`, birth_date: p.birth_date || "" })} className="text-slate-400 hover:text-accent-700"><Pencil className="w-3.5 h-3.5" /></button></span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400"><Users2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />Aucun patient.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Nouveau patient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="patient-lastname" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Prénom</Label><Input data-testid="patient-firstname" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Date de naissance <span className="text-slate-400 font-normal">(facultative)</span></Label><FrDateInput value={f.birth_date} onChange={(v) => setF({ ...f, birth_date: v })} mode="birth" testid="patient-birthdate" className="mt-1" /></div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button data-testid="submit-patient" onClick={submit} className="bg-accent-600 hover:bg-accent-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {edit && (
        <Dialog open onOpenChange={() => setEdit(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">{edit.name}</DialogTitle></DialogHeader>
            <div><Label>Date de naissance</Label><FrDateInput value={edit.birth_date} onChange={(v) => setEdit({ ...edit, birth_date: v })} mode="birth" testid="edit-dob-input" className="mt-1" autoFocus /></div>
            <PatientAccessBlock patientId={edit.id} testid="patients-patient-access" />
            <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button><Button data-testid="save-dob" onClick={saveEdit} className="bg-accent-600 hover:bg-accent-700">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
